import {
    StyledHeaderCell,
    StyledInteractiveRow,
    StyledRowCell,
} from '@/components/decisionSupport/tables/RecommendationsTable.styled';
import { RecommendationsTableSkeleton } from '@/components/decisionSupport/tables/RecommendationsTableSkeleton';
import {
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {
    type ColumnDef,
    type Row,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ReactNode, memo, useCallback } from 'react';

type RecommendationsTableProps<T> = {
    data: T[];
    columns: ColumnDef<T>[];
    selectedValue: unknown;
    getRowValue: (row: T) => unknown;
    onRowSelect: (row: T) => void;
    emptyText?: string;
    emptyIcon?: ReactNode;
    loading?: boolean;
    disabledRowValues?: Set<unknown>;
};

type RowProps<T> = {
    row: Row<T>;
    isSelected: boolean;
    isLast: boolean;
    isDisabled: boolean;
    onSelect: (row: T) => void;
};

const RowInner = <T,>({ row, isSelected, isLast, isDisabled, onSelect }: RowProps<T>) => {
    const handleClick = useCallback(() => onSelect(row.original), [onSelect, row]);
    const cells = row.getVisibleCells();

    return (
        <StyledInteractiveRow
            selected={isSelected}
            onClick={isDisabled ? undefined : handleClick}
            isDisabled={isDisabled}
        >
            {cells.map((cell, index) => {
                const isFirst = index === 0;
                const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
                return (
                    <StyledRowCell
                        key={cell.id}
                        align={isFirst ? 'left' : 'center'}
                        isFirst={isFirst}
                        isLast={isLast}
                    >
                        {isFirst ? (
                            <Stack direction='row' alignItems='center' spacing={1}>
                                <Radio checked={isSelected} size='small' disabled={isDisabled} />
                                {rendered}
                            </Stack>
                        ) : (
                            rendered
                        )}
                    </StyledRowCell>
                );
            })}
        </StyledInteractiveRow>
    );
};

const MemoRow = memo(RowInner) as typeof RowInner;

export const RecommendationsTable = <T,>(props: RecommendationsTableProps<T>) => {
    const {
        data,
        columns,
        selectedValue,
        getRowValue,
        onRowSelect,
        emptyText = 'No Recommendations Found',
        emptyIcon,
        loading = false,
        disabledRowValues,
    } = props;

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => String(getRowValue(row)),
    });

    const rows = table.getRowModel().rows;
    const headerGroups = table.getHeaderGroups();

    if (loading) {
        return <RecommendationsTableSkeleton />;
    }

    return (
        <TableContainer sx={{ overflow: 'visible' }}>
            <Table sx={{ width: 'auto' }}>
                <TableHead>
                    {headerGroups.map((headerGroup) => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map((header, index) => {
                                const isFirst = index === 0;
                                return (
                                    <StyledHeaderCell
                                        key={header.id}
                                        align={isFirst ? 'left' : 'center'}
                                        isFirst={isFirst}
                                    >
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </StyledHeaderCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={columns.length} align='center' sx={{ py: 10 }}>
                                {emptyIcon}
                                <Typography variant='body1' color='text.secondary'>
                                    {emptyText}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row, idx) => {
                            const rowValue = getRowValue(row.original);
                            const isSelected = rowValue === selectedValue;
                            const isDisabled =
                                !isSelected && (disabledRowValues?.has(rowValue) ?? false);
                            return (
                                <MemoRow
                                    key={row.id}
                                    row={row}
                                    isSelected={isSelected}
                                    isLast={idx === rows.length - 1}
                                    isDisabled={isDisabled}
                                    onSelect={onRowSelect}
                                />
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};
